import { useQuery } from '@tanstack/react-query'
import { ShoppingCart, Coins, RotateCcw, Bell } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency, formatDateTime } from '../lib/utils'

interface AbandonedCart {
  user_id: string
  full_name: string
  phone: string
  item_count: number
  total_fils: number
  updated_at: string
  notified_at: string | null
}

interface Response {
  carts: AbandonedCart[]
  open_count: number
  open_value_fils: number
  recovered_last_7d: number
}

export default function AbandonedCarts() {
  const { data, isLoading } = useQuery<Response>({
    queryKey: ['abandoned-carts'],
    queryFn: () => api.get('/analytics/abandoned-carts').then(r => r.data),
    refetchInterval: 60_000,
  })

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <ShoppingCart size={24} className="text-[#C41E3A]" />
        <h1 className="text-2xl font-bold text-white">Abandoned Carts</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Kpi icon={<ShoppingCart size={18} />} label="Open carts" value={`${data?.open_count ?? 0}`} sublabel="currently abandoned" />
        <Kpi icon={<Coins size={18} />} label="Total value" value={formatCurrency(data?.open_value_fils ?? 0)} sublabel="potential recovery" />
        <Kpi icon={<RotateCcw size={18} />} label="Recovered" value={`${data?.recovered_last_7d ?? 0}`} sublabel="in last 7 days (notified → ordered within 24h)" />
      </div>

      <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-[#2e2e2e] flex items-center justify-between">
          <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Open carts</h3>
          <span className="text-[10px] text-gray-500">Auto-refreshes every minute</span>
        </div>

        {isLoading && <p className="text-gray-400 text-sm px-4 py-6">Loading…</p>}
        {!isLoading && (data?.carts?.length ?? 0) === 0 && (
          <div className="px-4 py-12 text-center">
            <ShoppingCart size={32} className="mx-auto text-gray-600 mb-3" />
            <p className="text-gray-400 text-sm">No abandoned carts right now.</p>
          </div>
        )}

        {data?.carts && data.carts.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02]">
              <tr className="text-gray-500 text-[10px] uppercase tracking-wide">
                <th className="text-left px-4 py-2 font-semibold">Customer</th>
                <th className="text-right px-4 py-2 font-semibold">Items</th>
                <th className="text-right px-4 py-2 font-semibold">Value</th>
                <th className="text-left px-4 py-2 font-semibold">Last activity</th>
                <th className="text-left px-4 py-2 font-semibold">Notified</th>
              </tr>
            </thead>
            <tbody>
              {data.carts.map(c => (
                <tr key={c.user_id} className="border-t border-[#2e2e2e]">
                  <td className="px-4 py-3">
                    <p className="text-white truncate max-w-[240px]">{c.full_name}</p>
                    <p className="text-[10px] text-gray-500">{c.phone}</p>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-300 tabular-nums">{c.item_count}</td>
                  <td className="px-4 py-3 text-right text-white tabular-nums font-semibold">{formatCurrency(c.total_fils)}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{formatDateTime(c.updated_at)}</td>
                  <td className="px-4 py-3 text-xs">
                    {c.notified_at ? (
                      <span className="inline-flex items-center gap-1 text-amber-400">
                        <Bell size={11} /> {formatDateTime(c.notified_at)}
                      </span>
                    ) : (
                      <span className="text-gray-600">Not yet</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function Kpi({ icon, label, value, sublabel }: { icon: React.ReactNode; label: string; value: string; sublabel?: string }) {
  return (
    <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400 text-xs">{label}</span>
        <span className="text-[#C41E3A]">{icon}</span>
      </div>
      <p className="text-xl font-bold text-white tabular-nums">{value}</p>
      {sublabel && <p className="text-[10px] text-gray-500 mt-1">{sublabel}</p>}
    </div>
  )
}
